import SwapVertIcon from '@mui/icons-material/SwapVert';
import { Autocomplete,Button,Card,Dialog,DialogActions,DialogContent,DialogTitle,Skeleton,Stack,TextField,Typography } from '@mui/material';
import { useMutation,useQuery } from '@tanstack/react-query';
import { useContext,useEffect,useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { NewFormContext } from '../../context/NewFormContext';
import AttToken from '../../helpers/attToken';
import Checklists from '../../service/Checklists';
import Clients from '../../service/Clients';
import queryClient from '../../service/query';
import ServiceTypes from '../../service/ServiceTypes';
import { ChecklistType } from '../../types/checklist/types';
import type { serviceTypeResponse } from '../../types/services/types';
import GenericAcordonPlaces from '../GenericAcordeonPlaces/GenericAcordeonPlaces';
import { WarningSpan } from '../PrintTemplate/FormStyle';

const serviceType = new ServiceTypes();
const CustomerService = new Clients();
const ChecklistService = new Checklists();

export default function Servico({ mutate: parentMutate }: { mutate: Function }) {
  const { modelo, setModelo } = useContext(NewFormContext);
  const { t } = useTranslation('translation');

  const { data: serviceTypesData, isLoading: serviceTypesLoading, isFetching: serviceTypesFetching } = useQuery({
    queryKey: ['GET_SERVICE_TYPES'],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        if (token) {
          const response: serviceTypeResponse[] = await serviceType.getAllByCompany({ ...token, empresa: token.userEmpresa });
          if (!response) throw new Error('Erro ao buscar tipos de serviço');
          return response;
        }
      } catch (error) {
        console.error('Error fetching service types:', error);
        throw error;
      }
    },
    enabled: !modelo?.tipoServico?.length || !modelo?.nomeTipoServico?.length,
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  const servico = serviceTypesData?.find((tipo) => tipo.tipoId === modelo?.tipoServico)?.nome;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['GET_CLIENTES_CHILDREN', modelo?.tipoServico],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        if (token && modelo?.tipoServico) {
          const response = await CustomerService.getAllWithSets(token, modelo?.tipoServico);
          if (!response) throw new Error('Erro ao buscar clientes e conjuntos');
          return response;
        }
      } catch (error) {
        console.error('Error fetching clients and sets:', error);
        throw error;
      }
    },
    enabled: typeof modelo?.tipoServico === 'string' && modelo?.tipoServico?.length > 0,
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  function handleSetServiceType(value: { tipoId: string, nome: string }) {
    setModelo((prev: ChecklistType | null) => {
      if (prev) {
        return { ...prev, tipoServico: value.tipoId, nomeTipoServico: value.nome };
      }
      return prev;
    });
  }

  const [cnjsArr, setCnjsArr] = useState<string[]>(modelo?.conjuntosAssociados ? modelo.conjuntosAssociados || [] : []);
  const [confirmUnassociateOpen, setConfirmUnassociateOpen] = useState(false);

  useEffect(() => {
    if (cnjsArr) {
      setModelo((prev: ChecklistType | null) => {
        if (prev) {
          return { ...prev, conjuntosAssociados: cnjsArr };
        }
        return prev;
      });
    }
  }, [cnjsArr, setModelo]);

  const { mutate, isPending } = useMutation({
    mutationKey: ['UNASSOCIATE_SERVICE_TYPE', modelo],
    mutationFn: async () => {
      try {
        const promise = toast.promise(async () => {
          const token = await AttToken();
          if (!token) throw new Error('Token not found');
          if (token) {
            const response = await ChecklistService.unassociateType({ ...token, formId: modelo?.id as string, typeId: modelo?.tipoServico as string });
            if (response.message !== 'tipo de serviço desassociado') throw new Error('Erro ao desassociar tipo de serviço');
            if (!response) throw new Error('Erro ao desassociar tipo de serviço');
            return response;
          }
        }, {
          pending: t('associa.desassocia'),
          success: t('associa.associa'),
          error: t('associa.erroAssocia')
        });
        return promise;
      } catch (error) {
        console.error('Error unassociating service type:', error);
        throw error;
      }
    },
    onSuccess: () => {
      flushSync(() => {
        setModelo((prev: ChecklistType) => prev ? { ...prev, tipoServico: '', nomeTipoServico: '', conjuntosAssociados: null } : null);
        setCnjsArr([]);
      });
      queryClient.resetQueries({ queryKey: ['GET_SERVICE_TYPES'] });
      parentMutate();
    },
  });

  const onLoading = isLoading || isFetching || serviceTypesLoading || serviceTypesFetching || isPending;

  const loadigChildren = isLoading || isFetching;

  return (
    <Card sx={{ width: '100%', padding: 2 }}>
      <Stack
        width='100%'
        direction="column"
        justifyContent="center"
        alignItems='flex-start'
        spacing={2}
      >
        <Typography color='textDisabled' variant='h5' textAlign='center' fontWeight={600} width='100%'>{t('formDetails.Association.Associacao_de_Servico')}</Typography>
        {!modelo?.tipoServico?.length && <Stack
          width='100%'
          direction="column"
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
        >
          <Autocomplete
            fullWidth
            loading={onLoading}
            getOptionLabel={option => option.nome}
            getOptionKey={option => option?.tipoId}
            isOptionEqualToValue={(option, value) => option.tipoId === value.tipoId}
            options={
              serviceTypesData
                ?.sort((a, b) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1; })
                ?.map((tipo) => ({
                  ...tipo
                })) || []}
            onChange={(_event, newValue) => {
              handleSetServiceType(newValue as { tipoId: string, nome: string });
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.tipoId}>
                <span>{option.nome}</span>
              </li>)}
            renderInput={(params) => <TextField {...params} label={t('formDetails.Association.Tipo_de_servico_do_formulario')} />}
          />
          <Typography color='textDisabled' fontSize='medium' textAlign='center' width='100%'>
            {t('formDetails.Association.Por_favor_selecione_serviço')}
          </Typography>
        </Stack>}
        {modelo?.tipoServico && modelo?.tipoServico?.length > 0
          && <Stack
            width='100%'
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            spacing={1}
          >
            {onLoading
              ? <Skeleton variant='rounded' width='100%' height={40} />
              : <TextField fullWidth disabled label='Tipo de serviço' value={modelo?.nomeTipoServico ?? servico} />}
            <Button
              variant='outlined'
              color='error'
              endIcon={<SwapVertIcon />}
              onClick={() => setConfirmUnassociateOpen(true)}
              disabled={isPending}
            >
              {t('formDetails.Association.desassociar')}
            </Button>
          </Stack>}
        {loadigChildren
          ? <Skeleton variant='rounded' height={40} width={'100%'} />
          : <>
            {data && modelo?.tipoServico
              ? <GenericAcordonPlaces
                isLoading={loadigChildren}
                clients={data}
                conjuntosArray={cnjsArr}
                setConjuntosArray={setCnjsArr} />
              : modelo?.tipoServico && <WarningSpan>
                {t('formDetails.Association.Voce_nao_possui_nenhuma_estrutura')}
              </WarningSpan>}
          </>
        }
      </Stack>
      <Dialog open={confirmUnassociateOpen} onClose={() => !isPending && setConfirmUnassociateOpen(false)}>
        <DialogTitle>Desassociar tipo de serviço?</DialogTitle>
        <DialogContent>
          <Typography>Deseja realmente desassociar este tipo de serviço do formulário?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnassociateOpen(false)} disabled={isPending}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={isPending}
            onClick={() => mutate(undefined, { onSuccess: () => setConfirmUnassociateOpen(false) })}
          >
            Desassociar
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
